import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { ArrowLeft, Mail, Server, Save, Trash2, Check, X } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export default function AdminSettings() {
  const navigate = useNavigate();
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [loading, setLoading] = useState(false);
  const [smtpData, setSmtpData] = useState({
    host: "",
    port: 587,
    username: "",
    password: "",
    from_email: "",
    reminder_time: "08:00",
  });

  useEffect(() => {
    fetchSmtpSettings();
  }, []);

  const fetchSmtpSettings = async () => {
    try {
      const response = await axios.get("/settings/smtp");
      if (response.data.configured) {
        setSmtpConfigured(true);
        setSmtpData({
          host: response.data.host || "",
          port: response.data.port || 587,
          username: response.data.username || "",
          password: "",
          from_email: response.data.from_email || "",
          reminder_time: response.data.reminder_time || "08:00",
        });
      }
    } catch (error) {
      toast.error("Fehler beim Laden der Einstellungen");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post("/settings/smtp", smtpData);
      toast.success("SMTP-Einstellungen gespeichert!");
      setSmtpConfigured(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete("/settings/smtp");
      toast.success("SMTP-Einstellungen gelöscht");
      setSmtpConfigured(false);
      setSmtpData({
        host: "",
        port: 587,
        username: "",
        password: "",
        from_email: "",
        reminder_time: "08:00",
      });
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleTestSmtp = async () => {
    if (!smtpConfigured) {
      toast.error("Bitte speichern Sie zuerst die SMTP-Einstellungen");
      return;
    }
  };

  const handleTestReminders = async () => {
      if (!smtpConfigured) {
        toast.error("Bitte speichern Sie zuerst die SMTP-Einstellungen");
        return;
      }
  
      setLoading(true);
      try {
        const response = await axios.post("/settings/smtp/test-reminders");
        if (response.data.cards && response.data.cards.length > 0) {
          toast.success(
            `${response.data.message}\nKarten: ${response.data.cards.join(', ')}`,
            { duration: 5000 }
          );
        } else {
          toast.info(response.data.message);
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || "Erinnerungs-Test fehlgeschlagen");
      } finally {
        setLoading(false);
      }
    };
    
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-button"
              variant="outline"
              size="icon"
              onClick={() => navigate("/")}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">Admin-Einstellungen</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* SMTP Configuration Status */}
        {smtpConfigured && (
          <Card className="mb-6 border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-green-900">E-Mail-Versand aktiv</p>
                    <p className="text-sm text-green-700">SMTP ist konfiguriert und einsatzbereit</p>
                  </div>
                </div>
                <Button
                  onClick={handleTestSmtp}
                  disabled={loading}
                  variant="outline"
                  className="gap-2 border-green-600 text-green-700 hover:bg-green-100"
                >
                  <Mail className="w-4 h-4" />
                  {loading ? "Senden..." : "SMTP Test"}
                </Button>
                <Button
                    onClick={handleTestReminders}
                    disabled={loading}
                    variant="outline"
                    className="gap-2 border-blue-600 text-blue-700 hover:bg-blue-100"
                  >
                    <Mail className="w-4 h-4" />
                    {loading ? "Senden..." : "Erinnerungen Test"}
                  </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* SMTP Settings Form */}
        <Card data-testid="smtp-settings-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Server className="w-5 h-5" />
                  SMTP-Konfiguration
                </CardTitle>
                <CardDescription>
                  Konfiguriere deine E-Mail-Server für Benachrichtigungen
                </CardDescription>
              </div>
              {smtpConfigured && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" size="sm" className="gap-2">
                      <Trash2 className="w-4 h-4" />
                      Löschen
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>SMTP-Einstellungen löschen?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Diese Aktion löscht die SMTP-Konfiguration. E-Mail-Benachrichtigungen werden deaktiviert.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                        Löschen
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="host">SMTP-Host *</Label>
                  <Input
                    id="host"
                    data-testid="smtp-host-input"
                    placeholder="smtp.gmail.com"
                    value={smtpData.host}
                    onChange={(e) => setSmtpData({ ...smtpData, host: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="port">Port *</Label>
                  <Input
                    id="port"
                    data-testid="smtp-port-input"
                    type="number"
                    placeholder="587"
                    value={smtpData.port}
                    onChange={(e) => setSmtpData({ ...smtpData, port: parseInt(e.target.value) })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="username">Benutzername *</Label>
                <Input
                  id="username"
                  data-testid="smtp-username-input"
                  placeholder="your-email@gmail.com"
                  value={smtpData.username}
                  onChange={(e) => setSmtpData({ ...smtpData, username: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Passwort *</Label>
                <Input
                  id="password"
                  data-testid="smtp-password-input"
                  type="password"
                  placeholder={smtpConfigured ? "••••••••" : "Ihr Passwort"}
                  value={smtpData.password}
                  onChange={(e) => setSmtpData({ ...smtpData, password: e.target.value })}
                  required={!smtpConfigured}
                />
                {smtpConfigured && (
                  <p className="text-sm text-gray-500">Leer lassen, um das Passwort nicht zu ändern</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="from_email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Absender E-Mail *
                </Label>
                <Input
                  id="from_email"
                  data-testid="smtp-from-email-input"
                  type="email"
                  placeholder="noreply@ihredomain.de"
                  value={smtpData.from_email}
                  onChange={(e) => setSmtpData({ ...smtpData, from_email: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reminder_time">Erinnerungs-Uhrzeit</Label>
                <Input
                  id="reminder_time"
                  data-testid="smtp-reminder-time-input"
                  type="time"
                  value={smtpData.reminder_time}
                  onChange={(e) => setSmtpData({ ...smtpData, reminder_time: e.target.value })}
                  required
                />
                <p className="text-sm text-gray-500">
                  Tägliche Uhrzeit für Erinnerungs-E-Mails (z.B. 08:00 für 8 Uhr morgens)
                </p>
              </div>

              <Separator />

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">Hinweise:</h4>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>Für Gmail: Verwende ein App-Passwort (nicht Ihr reguläres Passwort)</li>
                  <li>Port 587 ist für STARTTLS (Standard)</li>
                  <li>Stelle sicher, dass dein E-Mail-Provider weniger sichere Apps zulässt</li>
                  <li><strong>E-Mail-Benachrichtigungen werden täglich zur eingestellten Uhrzeit für fällige Aufgaben gesendet</strong></li>
                  <li><strong>Betreff: "Erinnerung deiner Task" - Inhalt enthält Task-Name und Beschreibung</strong></li>
                </ul>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/")}
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  data-testid="save-smtp-button"
                  disabled={loading}
                  className="gap-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                >
                  <Save className="w-4 h-4" />
                  {loading ? "Speichern..." : "Einstellungen speichern"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
